import type { BadgeTone } from '@/components/ui';
import type {
  DomainHealth,
  HealthResult,
  MarketingEmailAccount,
  MarketingEmailAccountInput,
} from '@/lib/types';

/*
 * Email accounts — the decisions the page and the form share.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES.** It decides what a payload carries and
 * how a stored fact is worded; the page and the modal own their requests.
 *
 * ⛔ **A PASSWORD NEVER ROUND-TRIPS.** The server returns `hasSmtpPassword` /
 * `hasImapPassword` and never the secret, so the form's password boxes always
 * open EMPTY and an empty box means "keep what is stored". The key is sent only
 * when somebody typed into it — a blank `smtpPassword` on a PATCH would be a
 * request to store an empty password, which is not what an untouched box means.
 */

export type SmtpSecurity = MarketingEmailAccount['smtpSecurity'];
export type ImapSecurity = NonNullable<MarketingEmailAccount['imapSecurity']>;

export const SMTP_SECURITY_OPTIONS: readonly { value: SmtpSecurity; label: string }[] = [
  { value: 'starttls', label: 'STARTTLS' },
  { value: 'tls', label: 'TLS (implicit)' },
  { value: 'none', label: 'None' },
];

export const IMAP_SECURITY_OPTIONS: readonly { value: ImapSecurity; label: string }[] = [
  { value: 'tls', label: 'TLS (implicit)' },
  { value: 'starttls', label: 'STARTTLS' },
  { value: 'none', label: 'None' },
];

/** The conventional port for each security mode. */
export const SMTP_PORT_PRESET: Record<SmtpSecurity, string> = {
  starttls: '587',
  tls: '465',
  none: '25',
};

export const IMAP_PORT_PRESET: Record<ImapSecurity, string> = {
  tls: '993',
  starttls: '143',
  none: '143',
};

/**
 * The port to show after the security mode changes.
 *
 * Only a blank port or one that is still a preset follows the mode: a port
 * somebody typed (2525, say) is theirs, and silently overwriting it would send
 * the account to a port nobody chose.
 */
export function portAfterSecurityChange(
  currentPort: string,
  nextPreset: string,
  presets: Readonly<Record<string, string>>,
): string {
  const trimmed = currentPort.trim();
  if (trimmed === '' || Object.values(presets).includes(trimmed)) return nextPreset;
  return currentPort;
}

export const LIMITS = {
  dailyMin: 1,
  dailyMax: 100_000,
  perMinuteMin: 1,
  perMinuteMax: 600,
  dailyDefault: '500',
  perMinuteDefault: '20',
} as const;

export const MAX_LENGTHS = {
  name: 120,
  fromName: 120,
  email: 320,
  host: 255,
  username: 320,
  dkimSelector: 63,
} as const;

export interface EmailAccountFormValues {
  name: string;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  smtpHost: string;
  smtpPort: string;
  smtpSecurity: SmtpSecurity;
  smtpUsername: string;
  /** Always opens empty. Empty on an edit = keep the stored password. */
  smtpPassword: string;
  imapHost: string;
  imapPort: string;
  /** `''` = not chosen. */
  imapSecurity: ImapSecurity | '';
  imapUsername: string;
  /** Always opens empty. Empty on an edit = keep the stored password. */
  imapPassword: string;
  /** Explicit request to remove the stored IMAP password (sends `null`). */
  clearImapPassword: boolean;
  dkimSelector: string;
  dailyLimit: string;
  perMinuteLimit: string;
  restricted: boolean;
  /** User ids as strings, the picker's currency. */
  allowedUserIds: string[];
  isActive: boolean;
}

export function toFormValues(account?: MarketingEmailAccount): EmailAccountFormValues {
  return {
    name: account?.name ?? '',
    fromName: account?.fromName ?? '',
    fromEmail: account?.fromEmail ?? '',
    replyTo: account?.replyTo ?? '',
    smtpHost: account?.smtpHost ?? '',
    smtpPort: account ? String(account.smtpPort) : SMTP_PORT_PRESET.starttls,
    smtpSecurity: account?.smtpSecurity ?? 'starttls',
    smtpUsername: account?.smtpUsername ?? '',
    smtpPassword: '',
    imapHost: account?.imapHost ?? '',
    imapPort:
      account?.imapPort === null || account?.imapPort === undefined
        ? account
          ? ''
          : IMAP_PORT_PRESET.tls
        : String(account.imapPort),
    imapSecurity: account ? (account.imapSecurity ?? '') : 'tls',
    imapUsername: account?.imapUsername ?? '',
    imapPassword: '',
    clearImapPassword: false,
    dkimSelector: account?.dkimSelector ?? '',
    dailyLimit: account ? String(account.dailyLimit) : LIMITS.dailyDefault,
    perMinuteLimit: account ? String(account.perMinuteLimit) : LIMITS.perMinuteDefault,
    restricted: account?.restricted ?? false,
    allowedUserIds: (account?.allowedUsers ?? []).map((user) => String(user.id)),
    isActive: account?.isActive ?? true,
  };
}

/**
 * The write keys a 422 may name that this form can mark. Everything else goes
 * to the form-level banner through `ApiError.routeFieldErrors`.
 */
export const KNOWN_WRITE_KEYS: readonly string[] = [
  'name',
  'fromName',
  'fromEmail',
  'replyTo',
  'smtpHost',
  'smtpPort',
  'smtpSecurity',
  'smtpUsername',
  'smtpPassword',
  'imapHost',
  'imapPort',
  'imapSecurity',
  'imapUsername',
  'imapPassword',
  'dkimSelector',
  'dailyLimit',
  'perMinuteLimit',
  'restricted',
  'allowedUserIds',
  'isActive',
];

/**
 * Folds an indexed path (`allowedUserIds.2`) onto the control that renders the
 * set, so a refusal about one member marks the picker instead of vanishing into
 * the banner.
 */
export function normaliseErrorPaths(fields: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [path, message] of Object.entries(fields)) {
    const head = path.split('.')[0] ?? path;
    if (!(head in result)) result[head] = message;
  }
  return result;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function integerIn(raw: string, min: number, max: number): boolean {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return false;
  const value = Number(trimmed);
  return value >= min && value <= max;
}

function imapConfigured(values: EmailAccountFormValues): boolean {
  return values.imapHost.trim() !== '';
}

export function validate(
  values: EmailAccountFormValues,
  options: { isEdit: boolean; hasSmtpPassword: boolean },
): Record<string, string> {
  const found: Record<string, string> = {};

  if (values.name.trim() === '') found.name = 'A name is required.';
  if (values.fromName.trim() === '') found.fromName = 'A sender name is required.';
  if (values.fromEmail.trim() === '') found.fromEmail = 'A from address is required.';
  else if (!EMAIL_SHAPE.test(values.fromEmail.trim())) found.fromEmail = 'Enter a valid email address.';
  if (values.replyTo.trim() !== '' && !EMAIL_SHAPE.test(values.replyTo.trim())) {
    found.replyTo = 'Enter a valid email address.';
  }

  if (values.smtpHost.trim() === '') found.smtpHost = 'An SMTP host is required.';
  if (!integerIn(values.smtpPort, 1, 65_535)) found.smtpPort = 'Enter a port between 1 and 65535.';
  if (values.smtpUsername.trim() === '') found.smtpUsername = 'An SMTP username is required.';
  if (!options.isEdit && values.smtpPassword === '') {
    found.smtpPassword = 'An SMTP password is required.';
  }

  if (imapConfigured(values)) {
    if (!integerIn(values.imapPort, 1, 65_535)) found.imapPort = 'Enter a port between 1 and 65535.';
    if (values.imapSecurity === '') found.imapSecurity = 'Choose the IMAP security.';
    if (values.imapUsername.trim() === '') found.imapUsername = 'An IMAP username is required.';
  }

  if (!integerIn(values.dailyLimit, LIMITS.dailyMin, LIMITS.dailyMax)) {
    found.dailyLimit = `Enter a whole number from ${LIMITS.dailyMin} to ${LIMITS.dailyMax.toLocaleString()}.`;
  }
  if (!integerIn(values.perMinuteLimit, LIMITS.perMinuteMin, LIMITS.perMinuteMax)) {
    found.perMinuteLimit = `Enter a whole number from ${LIMITS.perMinuteMin} to ${LIMITS.perMinuteMax}.`;
  }

  return found;
}

function nullableText(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a);
  return b.every((value) => left.has(value));
}

/** The create body. Every required key is present; IMAP only when configured. */
export function buildCreatePayload(values: EmailAccountFormValues): MarketingEmailAccountInput {
  const payload: MarketingEmailAccountInput = {
    name: values.name.trim(),
    fromName: values.fromName.trim(),
    fromEmail: values.fromEmail.trim(),
    smtpHost: values.smtpHost.trim(),
    smtpPort: Number(values.smtpPort.trim()),
    smtpSecurity: values.smtpSecurity,
    smtpUsername: values.smtpUsername.trim(),
    smtpPassword: values.smtpPassword,
    dailyLimit: Number(values.dailyLimit.trim()),
    perMinuteLimit: Number(values.perMinuteLimit.trim()),
    restricted: values.restricted,
    isActive: values.isActive,
  };
  const replyTo = nullableText(values.replyTo);
  if (replyTo !== null) payload.replyTo = replyTo;
  const dkim = nullableText(values.dkimSelector);
  if (dkim !== null) payload.dkimSelector = dkim;
  if (values.allowedUserIds.length > 0) payload.allowedUserIds = values.allowedUserIds.map(Number);
  if (imapConfigured(values)) {
    payload.imapHost = values.imapHost.trim();
    payload.imapPort = Number(values.imapPort.trim());
    payload.imapSecurity = values.imapSecurity === '' ? null : values.imapSecurity;
    payload.imapUsername = values.imapUsername.trim();
    if (values.imapPassword !== '') payload.imapPassword = values.imapPassword;
  }
  return payload;
}

/**
 * The PATCH body: only what moved.
 *
 * ⚠️ **Emptying the IMAP host removes IMAP as a whole** — host, port, security,
 * username and the stored password all go out as `null` in ONE body. Clearing
 * the host alone would leave a half-configured mailbox the inbox worker then
 * fails against every five minutes.
 */
export function buildUpdatePayload(
  values: EmailAccountFormValues,
  baseline: EmailAccountFormValues,
  account: MarketingEmailAccount,
): Partial<MarketingEmailAccountInput> {
  const payload: Partial<MarketingEmailAccountInput> = {};
  const changed = (key: keyof EmailAccountFormValues) => {
    const next = values[key];
    const previous = baseline[key];
    return typeof next === 'string' && typeof previous === 'string'
      ? next.trim() !== previous.trim()
      : next !== previous;
  };

  if (changed('name')) payload.name = values.name.trim();
  if (changed('fromName')) payload.fromName = values.fromName.trim();
  if (changed('fromEmail')) payload.fromEmail = values.fromEmail.trim();
  if (changed('replyTo')) payload.replyTo = nullableText(values.replyTo);
  if (changed('smtpHost')) payload.smtpHost = values.smtpHost.trim();
  if (changed('smtpPort')) payload.smtpPort = Number(values.smtpPort.trim());
  if (changed('smtpSecurity')) payload.smtpSecurity = values.smtpSecurity;
  if (changed('smtpUsername')) payload.smtpUsername = values.smtpUsername.trim();
  // Typed into = replace. Untouched = keep. Never sent empty.
  if (values.smtpPassword !== '') payload.smtpPassword = values.smtpPassword;

  const wasConfigured = imapConfigured(baseline);
  const nowConfigured = imapConfigured(values);
  if (wasConfigured && !nowConfigured) {
    payload.imapHost = null;
    payload.imapPort = null;
    payload.imapSecurity = null;
    payload.imapUsername = null;
    if (account.hasImapPassword) payload.imapPassword = null;
  } else if (nowConfigured) {
    if (changed('imapHost')) payload.imapHost = values.imapHost.trim();
    if (changed('imapPort')) payload.imapPort = Number(values.imapPort.trim());
    if (changed('imapSecurity')) {
      payload.imapSecurity = values.imapSecurity === '' ? null : values.imapSecurity;
    }
    if (changed('imapUsername')) payload.imapUsername = nullableText(values.imapUsername);
    if (values.imapPassword !== '') payload.imapPassword = values.imapPassword;
    else if (values.clearImapPassword && account.hasImapPassword) payload.imapPassword = null;
  }

  if (changed('dkimSelector')) payload.dkimSelector = nullableText(values.dkimSelector);
  if (changed('dailyLimit')) payload.dailyLimit = Number(values.dailyLimit.trim());
  if (changed('perMinuteLimit')) payload.perMinuteLimit = Number(values.perMinuteLimit.trim());
  if (changed('restricted')) payload.restricted = values.restricted;
  // Whole-set replace: sent only when the SET moved, so `[]` is reachable only
  // by somebody removing the last user from a list that had one.
  if (!sameSet(values.allowedUserIds, baseline.allowedUserIds)) {
    payload.allowedUserIds = values.allowedUserIds.map(Number);
  }
  if (changed('isActive')) payload.isActive = values.isActive;
  return payload;
}

/* ── Display ──────────────────────────────────────────────────────────────── */

export const HEALTH_TONE: Record<HealthResult['status'], BadgeTone> = {
  pass: 'success',
  warn: 'warning',
  fail: 'danger',
};

export const HEALTH_STATUS_LABEL: Record<HealthResult['status'], string> = {
  pass: 'Pass',
  warn: 'Warning',
  fail: 'Fail',
};

export type HealthRecordKey = 'spf' | 'dkim' | 'dmarc';

export const HEALTH_RECORDS: readonly { key: HealthRecordKey; label: string }[] = [
  { key: 'spf', label: 'SPF' },
  { key: 'dkim', label: 'DKIM' },
  { key: 'dmarc', label: 'DMARC' },
];

/**
 * What to change, per record, when it does not pass. A fact about the fix
 * rather than advice beside a control — it renders only inside the health panel
 * and only for a record that failed or warned.
 */
export const HEALTH_FIX: Record<HealthRecordKey, string> = {
  spf: 'Add the mail provider to the domain’s SPF TXT record (an include: for the SMTP host), keeping a single v=spf1 record.',
  dkim: 'Publish the provider’s DKIM public key as a TXT record at <selector>._domainkey.<domain>, and set the DKIM selector on this account.',
  dmarc: 'Publish a TXT record at _dmarc.<domain> — start with v=DMARC1; p=none; and tighten once SPF and DKIM pass.',
};

export function healthResultOf(health: DomainHealth, key: HealthRecordKey): HealthResult {
  return health[key];
}

export function staffLabel(user: { name: string; email: string }): string {
  return user.name.trim() || user.email;
}

export const SMTP_FAILED_CODE = 'MARKETING_SMTP_FAILED';
export const IMAP_FAILED_CODE = 'MARKETING_IMAP_FAILED';
export const ACCOUNT_IN_USE_CODE = 'MARKETING_ACCOUNT_IN_USE';
export const ENCRYPTION_NOT_CONFIGURED_CODE = 'MARKETING_ENCRYPTION_NOT_CONFIGURED';

export const ENCRYPTION_NOT_CONFIGURED_MESSAGE =
  'Passwords cannot be saved yet: the server has no encryption key configured (SECRETS_ENCRYPTION_KEY). Nothing was saved. Ask whoever runs the server to set it.';
